/**
 * SkipConfirmDialog - Confirmation dialog for skipping next delivery
 */
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Box,
  Typography,
  Stack,
  Divider,
  Alert,
} from "@mui/material";
import SkipNextIcon from "@mui/icons-material/SkipNext";
import EventIcon from "@mui/icons-material/Event";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { QuizButton } from "./QuizButton";
import { PawsLoader } from "./PawsLoader";
import { fmtDate } from "../../utils/format";
import { QUIZ } from "@/theme/quizTokens";

type Props = {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  nextChargeDate: string | null;
  subscriptionTitle: string;
  loading?: boolean;
  success?: boolean;
  newDeliveryDate?: string | null;
};

export function SkipConfirmDialog({
  open,
  onClose,
  onConfirm,
  nextChargeDate,
  subscriptionTitle,
  loading = false,
  success = false,
  newDeliveryDate,
}: Props) {
  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: { xs: "16px", md: "20px" },
          background: QUIZ.color.cream,
        },
      }}
    >
      <DialogTitle
        sx={{
          background: success
            ? "linear-gradient(135deg, #E5FFEA 0%, #FAF5EB 100%)"
            : "linear-gradient(135deg, #E9DFD7 0%, #FAF5EB 100%)",
          borderBottom: "2px solid rgba(123, 51, 6, 0.1)",
          pb: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: "12px",
              background: success ? "#2E7D32" : QUIZ.color.brown,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {success ? (
              <CheckCircleIcon sx={{ fontSize: 28, color: QUIZ.color.white }} />
            ) : (
              <SkipNextIcon sx={{ fontSize: 28, color: QUIZ.color.white }} />
            )}
          </Box>
          <Box>
            <Typography
              sx={{
                fontSize: { xs: 20, md: 22 },
                fontWeight: 800,
                color: QUIZ.color.brown,
                lineHeight: 1.2,
              }}
            >
              {success ? "Delivery Skipped!" : "Skip Next Delivery?"}
            </Typography>
            <Typography
              sx={{
                fontSize: { xs: 13, md: 14 },
                color: QUIZ.color.brown,
                opacity: 0.65,
                mt: 0.5,
              }}
            >
              {subscriptionTitle}
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: { xs: 2.5, md: 3 }, background: QUIZ.color.cream }}>
        {/* Loading State */}
        {loading && !success && (
          <PawsLoader
            action="skip"
            fullScreen={false}
            size="small"
          />
        )}

        {/* Success State */}
        {success && newDeliveryDate && (
          <>
            <Alert
              severity="success"
              icon={<CheckCircleIcon />}
              sx={{
                borderRadius: "12px",
                mb: 2,
                "& .MuiAlert-icon": { alignItems: "center" },
              }}
            >
              Your delivery has been skipped successfully!
            </Alert>

            <Box
              sx={{
                p: 2,
                borderRadius: "12px",
                background: "#E5FFEA",
                border: "2px solid #2E7D32",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
                <EventIcon sx={{ fontSize: 20, color: "#2E7D32" }} />
                <Typography
                  sx={{
                    fontSize: 14,
                    fontWeight: 700,
                    color: "#2E7D32",
                  }}
                >
                  Your New Next Delivery
                </Typography>
              </Box>
              <Typography
                sx={{
                  fontSize: { xs: 20, md: 24 },
                  fontWeight: 800,
                  color: QUIZ.color.brown,
                }}
              >
                {fmtDate(newDeliveryDate)}
              </Typography>
            </Box>

            <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
              <QuizButton variant="primary" onClick={onClose} fullWidth>
                Done
              </QuizButton>
            </Stack>
          </>
        )}

        {/* Confirmation State */}
        {!success && !loading && (
          <>
            <Alert
              severity="info"
              sx={{
                borderRadius: "12px",
                mb: 2,
                "& .MuiAlert-icon": { alignItems: "center" },
              }}
            >
              This will skip delivery for all your subscriptions (meals and
              treats) at once.
            </Alert>

            {/* Current Next Charge */}
            <Box
              sx={{
                p: 2,
                borderRadius: "12px",
                background: QUIZ.color.warmWhite,
                border: "2px solid rgba(123, 51, 6, 0.1)",
                mb: 2,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
                <EventIcon sx={{ fontSize: 20, color: QUIZ.color.brown }} />
                <Typography
                  sx={{
                    fontSize: 14,
                    fontWeight: 700,
                    color: QUIZ.color.brown,
                  }}
                >
                  Current Next Delivery
                </Typography>
              </Box>
              <Typography
                sx={{
                  fontSize: { xs: 18, md: 20 },
                  fontWeight: 800,
                  color: QUIZ.color.brown,
                }}
              >
                {nextChargeDate ? fmtDate(nextChargeDate) : "Not scheduled"}
              </Typography>
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* What Will Happen */}
            <Box
              sx={{
                p: 2,
                borderRadius: "12px",
                background: QUIZ.color.blue,
                border: "1px solid rgba(26, 87, 211, 0.15)",
              }}
            >
              <Typography
                sx={{
                  fontSize: 14,
                  fontWeight: 700,
                  color: "#1A57D3",
                  mb: 1,
                }}
              >
                What will happen:
              </Typography>
              <Stack spacing={1}>
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Typography sx={{ fontSize: 14, color: QUIZ.color.brown, fontWeight: 700 }}>
                    •
                  </Typography>
                  <Typography sx={{ fontSize: 14, color: QUIZ.color.brown }}>
                    Your next delivery will be <strong>skipped</strong>
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Typography sx={{ fontSize: 14, color: QUIZ.color.brown, fontWeight: 700 }}>
                    •
                  </Typography>
                  <Typography sx={{ fontSize: 14, color: QUIZ.color.brown }}>
                    The following delivery will become your new next charge
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Typography sx={{ fontSize: 14, color: QUIZ.color.brown, fontWeight: 700 }}>
                    •
                  </Typography>
                  <Typography sx={{ fontSize: 14, color: QUIZ.color.brown }}>
                    Your subscription will continue as normal after that
                  </Typography>
                </Box>
              </Stack>
            </Box>

            {/* Actions */}
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mt: 3 }}>
              <QuizButton
                variant="secondary"
                onClick={onClose}
                fullWidth
                sx={{ flex: 1 }}
                disabled={loading}
              >
                Cancel
              </QuizButton>
              <QuizButton
                variant="primary"
                onClick={onConfirm}
                fullWidth
                sx={{ flex: 1 }}
                disabled={loading}
                startIcon={loading ? undefined : <SkipNextIcon />}
              >
                {loading ? "Skipping..." : "Yes, Skip Delivery"}
              </QuizButton>
            </Stack>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
